"use client"

import { useState, useMemo } from "react"
import { BENCHMARKS, DEFAULT_FORM_STATE, type ProfitFormData, type RestaurantSegment } from "./data"
import InputsPanel from "./InputsPanel"
import PerformanceSummary from "./PerformanceSummary"

const formatUSD = (val: number) => `$${Math.round(val).toLocaleString("en-US")}`

export default function RestaurantProfitMarginCalculator() {
   const [formData, setFormData] = useState<ProfitFormData>(DEFAULT_FORM_STATE)

   const handleInputChange = (field: keyof ProfitFormData, value: string) => {
      if (field === "segment") {
         setFormData((prev) => ({ ...prev, segment: value as RestaurantSegment }))
      } else {
         const parsed = parseFloat(value)
         setFormData((prev) => ({
            ...prev,
            [field]: isNaN(parsed) ? 0 : parsed,
         }))
      }
   }

   const resetDefaults = () => {
      setFormData(DEFAULT_FORM_STATE)
   }

   // Perform All Financial Metric Calculations Dynamically
   const metrics = useMemo(() => {
      const totalRevenue = formData.foodSales + formData.bevSales + formData.cateringSales
      const totalCOGS = formData.foodCost + formData.bevCost
      const fixedExpenses = formData.rentCost + formData.utilitiesCost + formData.marketingCost + formData.otherCost
      const totalExpenses = totalCOGS + formData.laborCost + fixedExpenses
      const netProfitValue = totalRevenue - totalExpenses

      if (totalRevenue === 0) {
         return {
            totalRevenue: 0,
            totalExpenses: 0,
            netProfitValue: 0,
            totalCOGS: 0,
            fixedExpenses: 0,
            grossProfitMargin: 0,
            netProfitMargin: 0,
            primeCostRatio: 0,
            breakEvenAmount: 0,
            netStatus: { text: "Waiting for input...", style: "text-slate-400" },
            grossStatus: { text: "Waiting for input...", style: "text-slate-400" },
            primeStatus: { text: "Waiting for input...", style: "text-slate-400" },
         }
      }

      const grossProfitMargin = ((totalRevenue - totalCOGS) / totalRevenue) * 100
      const netProfitMargin = (netProfitValue / totalRevenue) * 100
      const primeCostRatio = ((totalCOGS + formData.laborCost) / totalRevenue) * 100
      const breakEvenAmount = grossProfitMargin > 0 ? fixedExpenses / (grossProfitMargin / 100) : 0

      const bench = BENCHMARKS[formData.segment] || BENCHMARKS["Casual Dining"]

      // 1. Evaluate Net Profit Status
      let netStatus = { text: "Checking...", style: "text-slate-400" }
      if (netProfitMargin >= bench.maxNet) {
         netStatus = { text: "⭐ Above Segment Average", style: "text-emerald-400 font-bold" }
      } else if (netProfitMargin >= bench.minNet) {
         netStatus = { text: "✅ Within Optimal Limits", style: "text-emerald-400 font-semibold" }
      } else if (netProfitMargin > 0) {
         netStatus = { text: "⚠️ Low Net Cash Cushion", style: "text-amber-400 font-semibold" }
      } else {
         netStatus = {
            text: "🚨 Operating at a Deficit",
            style: "text-rose-400 font-bold animate-pulse",
         }
      }

      // 2. Evaluate Gross Profit Status
      let grossStatus = { text: "Checking...", style: "text-slate-400" }
      if (grossProfitMargin >= bench.minGross) {
         grossStatus = { text: "✅ Healthy Portion Margin", style: "text-emerald-400 font-semibold" }
      } else {
         grossStatus = {
            text: "⚠️ Check Spoilage & Vendor Rates",
            style: "text-rose-400 font-semibold",
         }
      }

      // 3. Evaluate Prime Cost Status
      let primeStatus = { text: "Checking...", style: "text-slate-400" }
      if (primeCostRatio <= bench.maxPrime) {
         primeStatus = { text: "✅ Well-Managed Prime Cost", style: "text-emerald-400 font-semibold" }
      } else {
         primeStatus = {
            text: "🚨 High Labor or Variable Cost Alert",
            style: "text-rose-400 font-bold animate-pulse",
         }
      }

      return {
         totalRevenue,
         totalExpenses,
         netProfitValue,
         totalCOGS,
         fixedExpenses,
         grossProfitMargin,
         netProfitMargin,
         primeCostRatio,
         breakEvenAmount,
         netStatus,
         grossStatus,
         primeStatus,
      }
   }, [formData])

   return (
      <div className="w-full max-w-full overflow-x-hidden px-4 py-6 md:p-8">
         {/* TWO COLUMN INTERACTIVE CALCULATOR */}
         <div className="grid grid-cols-1 items-start gap-8 lg:grid-cols-12">
            <InputsPanel formData={formData} onInputChange={handleInputChange} />
            <PerformanceSummary
               formData={formData}
               metrics={metrics}
               onSegmentChange={(value) => handleInputChange("segment", value)}
               onReset={resetDefaults}
               formatUSD={formatUSD}
            />
         </div>
      </div>
   )
}
